import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Mail } from 'lucide-react';
import MarketingLayout from '@/components/marketing/MarketingLayout';
import Seo from '@/components/shared/Seo';

/** Reusable section header used across marketing pages. */
export function SectionHeader({ eyebrow, title, subtitle, className = '' }) {
  return (
    <div className={`text-center ${className}`}>
      {eyebrow && (
        <span className="text-[11.5px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">{eyebrow}</span>
      )}
      <h1 className="dis mt-3 md:mt-4 text-[32px] md:text-[52px] font-bold leading-[1.08] tracking-[-0.03em] t1" style={{ fontFamily: "'Sora', sans-serif" }}>
        {title}
      </h1>
      {subtitle && (
        <p className="mx-auto mt-4 md:mt-5 max-w-[640px] text-[14.5px] md:text-[17px] leading-[24px] md:leading-[27px] t2">{subtitle}</p>
      )}
    </div>
  );
}

/** Reusable content card. */
export function ContentCard({ children, className = '', ...rest }) {
  return (
    <div className={`card softlg rounded-[22px] md:rounded-[26px] p-6 md:p-10 ${className}`} {...rest}>
      {children}
    </div>
  );
}

/** Reusable inline CTA bar. */
export function InlineCta({ onBookDemo, title, note }) {
  return (
    <div className="mt-14 md:mt-20 relative overflow-hidden rounded-[24px] md:rounded-[30px] p-8 md:p-12 aurora softlg" data-testid="inline-cta">
      <div className="pointer-events-none absolute inset-0 grid-bg opacity-15" />
      <div className="relative text-center">
        <h3 className="dis text-[24px] md:text-[34px] font-bold leading-[1.15] tracking-[-0.02em] text-white" style={{ fontFamily: "'Sora', sans-serif" }}>{title}</h3>
        {note && <p className="mt-3 text-[14px] md:text-[15.5px] text-white/85">{note}</p>}
        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <Link to="/login" className="rounded-full bg-white px-6 py-3 text-[14.5px] font-semibold text-[#6D5EF7] shadow-[0_16px_40px_rgba(17,19,42,.22)] hover:-translate-y-0.5 transition inline-flex items-center justify-center gap-2">
            Get started free <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <button onClick={onBookDemo} className="rounded-full border border-white/50 bg-white/10 px-6 py-3 text-[14.5px] font-semibold text-white backdrop-blur hover:bg-white/20 transition">
            Book a demo
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Visible FAQ block. Pair it with faqJsonLd() so the structured data always
 * mirrors on-page content (a Google + answer-engine requirement).
 */
export function FaqSection({ items, className = '' }) {
  return (
    <div className={`mt-14 md:mt-20 ${className}`} data-testid="faq-section">
      <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">
        <Sparkles className="w-4 h-4" /> Frequently asked
      </div>
      <div className="mt-5 grid md:grid-cols-2 gap-4 md:gap-5">
        {items.map((f, i) => (
          <div key={f.q} className="card thm softlg rounded-[20px] p-5 md:p-6" data-testid={`faq-item-${i}`}>
            <h3 className="dis text-[15.5px] md:text-[17px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{f.q}</h3>
            <p className="mt-2 text-[13.5px] md:text-[14px] leading-[22px] t3">{f.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Generic MarketingPage: composes MarketingLayout, Seo, section header + children.
 * Used by simple content pages so each page file stays tiny.
 */
export default function MarketingPage({
  seoTitle, seoDescription, seoPath, seoKeywords, jsonLd,
  eyebrow, title, subtitle, children, ctaTitle, ctaNote, faq,
}) {
  return (
    <MarketingLayout>
      {({ onBookDemo }) => (
        <>
          <Seo title={seoTitle} description={seoDescription} path={seoPath} keywords={seoKeywords} jsonLd={jsonLd} />
          <section className="mx-auto max-w-[1240px] px-4 md:px-6 pt-14 md:pt-24 pb-8">
            <SectionHeader eyebrow={eyebrow} title={title} subtitle={subtitle} />
          </section>
          <section className="mx-auto max-w-[1240px] px-4 md:px-6 pb-16">
            {children}
            {faq?.length > 0 && <FaqSection items={faq} />}
            <InlineCta
              onBookDemo={onBookDemo}
              title={ctaTitle || 'Ready to see Numuv in action?'}
              note={ctaNote || <>Reach us at <a href="mailto:hello@numuv.app" className="underline decoration-white/40">hello@numuv.app</a> or book a live walkthrough.</>}
            />
          </section>
        </>
      )}
    </MarketingLayout>
  );
}

export function ContactCallout() {
  return (
    <a href="mailto:hello@numuv.app" className="inline-flex items-center gap-2 rounded-full chip px-4 py-2 text-[13px] t2 hover:text-[#6D5EF7] transition" data-testid="contact-callout">
      <Mail className="w-4 h-4" /> hello@numuv.app
    </a>
  );
}

export function TinySparkleBadge({ text }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full chip px-4 py-1.5 text-[12px] md:text-[12.5px] font-medium t2">
      <Sparkles className="w-3.5 h-3.5 text-[#6D5EF7]" /> {text}
    </span>
  );
}
